import { EntityNameEnum } from '@ppl/graphql-space-api';
import { createObject, normalizeToObject } from '@ppl/utils/object.utils';
import { assertNever } from '@ppl/utils/typescript.utils';
import type { PplNamePipe } from '@ppl/auth';
import type {
  AccountEntity,
  AppointmentEntity,
  ClientEntity,
  ContactEntity,
  CustomEntityDescriptor,
  CustomEntityEntity,
  EntityCustomNameSetting,
  LeadEntity,
  OpportunityEntity,
  OpptyProductRelationEntity,
  ProductEntity,
  ProjectEntity,
  QuoteEntity,
  TaskEntity
} from '@ppl/graphql-space-api';

type Entity = AccountEntity
  | ContactEntity
  | LeadEntity
  | OpportunityEntity
  | TaskEntity
  | AppointmentEntity
  | ProjectEntity
  | QuoteEntity
  | ClientEntity
  | ProductEntity
  | OpptyProductRelationEntity
  | CustomEntityEntity;

export function getEntityName(entity: Entity, namePipe: PplNamePipe) {
  switch (entity.__typename) {
    case 'AccountEntity':
    case 'LeadEntity':
    case 'OpportunityEntity':
    case 'ProjectEntity':
    case 'QuoteEntity':
    case 'CustomEntityEntity':
    case 'ProductEntity':
      return entity.name;
    case 'ClientEntity':
    case 'ContactEntity':
      return namePipe.transform(entity);
    case 'AppointmentEntity':
    case 'TaskEntity':
      return entity.subject;
    case 'OpptyProductRelationEntity':
      return '';
    default:
      assertNever(entity);
  }
}

export function getEntityEntityName(entity: { __typename?: string; }) {
  return entity.__typename.replace('Entity', '') as EntityNameEnum;
}

export function getEntityEntityApiName(entity: Entity) {
  switch (entity.__typename) {
    case 'CustomEntityEntity':
      return entity.type?.entityApiName;
    default:
      return null;
  }
}

export function getEntityEffectiveEntityName(entityType: EntityType): string {
  return entityType.entityName === EntityNameEnum.CustomEntity
    ? entityType.entityApiName
    : entityType.entityName;
}

export interface EntityType<T = EntityNameEnum> {
  entityName: T;
  entityApiName: string | null;
}

export function createEntityTypeValue(entityType: EntityType): string {
  return entityType.entityApiName
    ? `${entityType.entityName}:${entityType.entityApiName}`
    : entityType.entityName;
}

export function createEntityTypeId(entityType: EntityType) {
  return `${entityType.entityName}:${entityType.entityApiName}`;
}

export function createEntityTypeLabelSingular(
  i18nService: { translateEnumValue: (enumName: string, key: any) => string; },
  entityType: EntityType,
  options: {
    customEntityDescriptor?: CustomEntityDescriptor;
  } = {}
): string {
  return entityType.entityApiName
    ? options.customEntityDescriptor
      ? options.customEntityDescriptor.nameSingular
      : entityType.entityApiName
    : i18nService.translateEnumValue('EntityNameEnum', entityType.entityName);
}

export const ACCOUNT_TYPE_ID = 'a456236f-6f23-0908-abfd-384aa0f62f9e';
export const CONTACT_TYPE_ID = '44f0f3a0-e998-03d8-88ac-69f61e213b02';
export const LEAD_TYPE_ID = '051d0778-8e59-05f1-aa33-10e5f793d893';
export const QUOTE_TYPE_ID = 'aa6a8ede-0315-462d-a792-5adfc8030278';
export const OPPORTUNITY_TYPE_ID = '0c03f112-4544-06a8-b162-1f14524c149c';
export const CALL_TYPE_ID = '5d451c07-a9a8-0df9-4812-d50057a0d491';
export const EMAIL_TYPE_ID = '3b6a66e9-568d-0ace-abcb-174b85b8fb4c';
export const TASK_TYPE_ID = '45e3f3cf-4a46-0159-1fd1-9314c4dcd2dc';
export const APPOINTMENT_TYPE_ID = '8baef00e-a17a-0905-f212-06a1280de149';
export const RECURRENT_APPOINTMENT_TYPE_ID = '714722b2-9111-0af3-2a4a-100a76c69285';
export const ACCOUNT_PLAN_PROJECT_TYPE_ID = 'ca955bc7-def9-4d31-94dc-ec451e639112';
export const DEFAULT_LEAD_PROCESS_ID = '9f2dcf79-bc7c-4733-b290-6ed29fb5bb7c';

export enum EntityCustomNameMarker {
  Account_s = 'Account_s',
  Account_p = 'Account_p',
  Contact_s = 'Contact_s',
  Contact_p = 'Contact_p',
  Lead_s = 'Lead_s',
  Lead_p = 'Lead_p',
  Opportunity_s = 'Opportunity_s',
  Opportunity_p = 'Opportunity_p',
  Quote_s = 'Quote_s',
  Quote_p = 'Quote_p',
  CustomEntity_s = 'CustomEntity_s',
  CustomEntity_p = 'CustomEntity_p',
  Project_s = 'Project_s',
  Project_p = 'Project_p'
}

interface EntityCustomName {
  singular: string;
  plural: string;
}

export type EntityCustomNames = { [K in EntityNameEnum]?: EntityCustomName };

export function entityNamesToMap(
  entityNames: EntityCustomNameSetting[]
): EntityCustomNames {
  return entityNames.reduce<EntityCustomNames>((prev, next) => ({
    ...prev,
    [next.entityName]: {
      singular: next.singular.current,
      plural: next.plural.current,
    }
  }), {} as any);
}

export function entityNamesToMarkers(
  entityNames: EntityCustomNameSetting[]
): { [key in EntityCustomNameMarker]?: string } {
  const entityNamesMap = normalizeToObject(entityNames, 'entityName');

  const markers = {
    [EntityCustomNameMarker.Account_s]: entityNamesMap[EntityNameEnum.Account].singular.current,
    [EntityCustomNameMarker.Account_p]: entityNamesMap[EntityNameEnum.Account].plural.current,
    [EntityCustomNameMarker.Contact_s]: entityNamesMap[EntityNameEnum.Contact].singular.current,
    [EntityCustomNameMarker.Contact_p]: entityNamesMap[EntityNameEnum.Contact].plural.current,
    [EntityCustomNameMarker.Lead_s]: entityNamesMap[EntityNameEnum.Lead].singular.current,
    [EntityCustomNameMarker.Lead_p]: entityNamesMap[EntityNameEnum.Lead].plural.current,
    [EntityCustomNameMarker.Opportunity_s]: entityNamesMap[EntityNameEnum.Opportunity].singular.current,
    [EntityCustomNameMarker.Opportunity_p]: entityNamesMap[EntityNameEnum.Opportunity].plural.current,
    [EntityCustomNameMarker.Quote_s]: entityNamesMap[EntityNameEnum.Quote].singular.current,
    [EntityCustomNameMarker.Quote_p]: entityNamesMap[EntityNameEnum.Quote].plural.current,
    [EntityCustomNameMarker.Project_s]: entityNamesMap[EntityNameEnum.Project].singular.current,
    [EntityCustomNameMarker.Project_p]: entityNamesMap[EntityNameEnum.Project].plural.current,
  };

  return markers;
}

export function customEntityNamesToMarkers(
  customEntityDescriptors: CustomEntityDescriptor[]
) {
  return createObject(customEntityDescriptors, (value) => [
    value.entityApiName,
    {
      [EntityCustomNameMarker.CustomEntity_s]: value.nameSingular,
      [EntityCustomNameMarker.CustomEntity_p]: value.namePlural,
    }
  ]);
}

export const entityPlurals = {
  [EntityNameEnum.Account]: 'Accounts',
  [EntityNameEnum.Activity]: 'Activities',
  [EntityNameEnum.ActivityComment]: 'Comments',
  [EntityNameEnum.Contact]: 'Contacts',
  [EntityNameEnum.CloudObject]: 'Documents',
  [EntityNameEnum.Email]: 'Emails',
  [EntityNameEnum.Lead]: 'Leads',
  [EntityNameEnum.LeadOppty]: 'Leads_&_Opportunities',
  [EntityNameEnum.Message]: 'Messages',
  [EntityNameEnum.Note]: 'Notes',
  [EntityNameEnum.OnlineForm]: 'Online_Forms',
  [EntityNameEnum.Opportunity]: 'Opportunities',
  [EntityNameEnum.Product]: 'Products',
  [EntityNameEnum.Project]: 'Projects',
  [EntityNameEnum.Quote]: 'Quotes',
  [EntityNameEnum.Timeframe]: 'Velocities',
};

export const RelationFieldMap = {
  [EntityNameEnum.Account]: 'accountId',
  [EntityNameEnum.Appointment]: 'activityId',
  [EntityNameEnum.Contact]: 'contactId',
  [EntityNameEnum.CustomEntity]: 'customEntityId',
  [EntityNameEnum.Lead]: 'leadOpptyId',
  [EntityNameEnum.LeadOppty]: 'leadOpptyId',
  [EntityNameEnum.Opportunity]: 'leadOpptyId',
  [EntityNameEnum.Profile]: 'profileId',
  [EntityNameEnum.Report]: 'reportId',
  [EntityNameEnum.Task]: 'activityId',
  [EntityNameEnum.Project]: 'projectId',
  [EntityNameEnum.Quote]: 'quoteId'
};

export const LEAD_OPPTY_ENTITY_NAMES = [EntityNameEnum.Lead, EntityNameEnum.LeadOppty, EntityNameEnum.Opportunity];
